import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { CloudDownload } from '@mui/icons-material';
import { isEmpty } from 'lodash';
import Button from '../../../../common/components/Button/Button';
import { Dialog } from '../../../../common/components/Dialog/Dialog';
import CloudUploadSVG from '../../../../images/Cloud-upload.svg';
import { StatusLabel } from '../../../../common/constants/CommonUsedDropdownsStatics';
import { DOWNLOAD, IN_PROGRESS_BATCH_UPDATE, sessionStatusesValues } from '../../../../common/constants/statuses';
import InfoButtonComponent from '../../../HomePage/components/InfoButtonComponent';
import APIClient from '../../../../common/services/APIClient';
import { fileDownload } from '../../../../common/services/fileUtils';
import { iotOnDateFormat } from '../../../../common/services/ValidationRules';

const URL_TO_GET_THE_FILE_RESULTS = '/chargers/details/import-results';
const URL_TO_DOWNLOAD_TEMPLATE = '/chargers/details/import-template';
const URL_TO_UPLOAD_FILE = 'chargers/details/import';
const MAX_FILE_SIZE_BYTES = 1048576;
const FILE_TYPE = 'text/csv';
const FILE_STATUS_MAP = {
  IN_PROGRESS: IN_PROGRESS_BATCH_UPDATE,
  COMPLETED: DOWNLOAD,
  ERROR: sessionStatusesValues.ERROR,
};

export const DetailsUpdateModal = ({ isOpen, onClose }) => {
  const fileInputRef = useRef(null);

  const [selectedFileName, setSelectedFileName] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isButtonDisabled, setButtonIsDisabled] = useState(true);
  const [error, setError] = useState(null);
  const [filesHistory, setFilesHistory] = useState([]);

  const handleFileDialogOpen = () => fileInputRef.current.click();
  const resetFormStatus = () => {
    setSelectedFileName(null);
    setButtonIsDisabled(true);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];

    if (!file) return;

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setError('Maximum file size is 1,048 KB. Update the file and try the Batch Update again.');
      resetFormStatus();

      return;
    }

    if (file.type !== FILE_TYPE) {
      setError('Please select only CSV file format.');
      resetFormStatus();

      return;
    }

    setSelectedFile(file);
    setSelectedFileName(file.name);
    setButtonIsDisabled(false);
    setError(null);
  };

  const handleModalClose = () => {
    onClose();

    resetFormStatus();
    setError(null);
  };

  const handleSubmit = async () => {
    try {
      const formData = new FormData();

      formData.append('file', selectedFile);

      const { status } = await APIClient.post(URL_TO_UPLOAD_FILE, formData);

      if (status !== 202) {
        setError('Oops ... We are working on it.');
        resetFormStatus();

        return;
      }

      handleModalClose();
    } catch (e) {
      console.log('handleSubmit Details Batch Update error: ', e);
    }
  };

  const actions = [
    <Button key="cancel" color="secondary" onClick={handleModalClose}>
      Cancel
    </Button>,
    <Button key="submit" color="primary" onClick={handleSubmit} disabled={isButtonDisabled}>
      Submit
    </Button>,
  ];

  const modalTitle = (
    <>
      Charger Details Batch Update
      <InfoButtonComponent infoOption="batchUpdateDetails" withoutSorting />
    </>
  );

  const handleDownloadTemplate = async () => {
    const { headers, data } = await APIClient.get(URL_TO_DOWNLOAD_TEMPLATE);

    fileDownload(data, 'template.csv', headers['text/csv']);
  };

  useEffect(() => {
    const getTheLast3Files = async () => {
      try {
        const { data } = await APIClient.get(URL_TO_GET_THE_FILE_RESULTS);

        setFilesHistory(data);
      } catch (e) {
        console.error('Get Files Error: ', e);
      }
    };

    if (isOpen) {
      getTheLast3Files();
    }
  }, [isOpen]);

  return (
    isOpen && (
      <Dialog title={modalTitle} actions={actions} open={isOpen} onClose={onClose} maxWidth="md" fullWidth>
        <div className="batch-update__select-container">
          <div className="batch-update__button" onClick={handleFileDialogOpen}>
            <img src={CloudUploadSVG} alt="Upload Icon" />
            <input type="file" accept=".csv" ref={fileInputRef} onChange={handleFileChange} className="batch-update__input" />
            Select File (.csv)
          </div>
          <div onClick={handleDownloadTemplate} className="link batch-update__link">
            Download Template
          </div>
        </div>

        {selectedFileName && (
          <div className="batch-update__selected-file">
            <span>* Selected File</span>
            <div className="batch-update__file-name">{selectedFileName}</div>
          </div>
        )}

        {error && <div className="batch-update__selected-file batch-update__selected-file--error">* {error}</div>}

        {!isEmpty(filesHistory) && (
          <div className="batch-update__table">
            <div className="batch-update__table-title">Recently Uploaded Files</div>
            <div className="batch-update__table-body">
              {filesHistory.map(({ file_name, import_ts, status, download_url }, index) => (
                <div key={`${file_name}-${index}`} className="batch-update__table-row">
                  {status === 'COMPLETED' ? (
                    <a download href={download_url} className="link batch-update__table-cell batch-update__table-cell--name">
                      <CloudDownload />
                      {file_name}
                    </a>
                  ) : (
                    <div className="batch-update__table-cell batch-update__table-cell--name">{file_name}</div>
                  )}
                  <div className="batch-update__table-cell batch-update__table-cell--date">
                    {moment(import_ts).utc().format(iotOnDateFormat)}
                  </div>
                  <div className="batch-update__table-cell batch-update__table-cell--status">
                    <StatusLabel status={FILE_STATUS_MAP[status]} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Dialog>
    )
  );
};
